import React from 'react';

/**
 * Per-area summary card on the Step 7 quote summary screen. Computes mixes
 * and coverage on the fly from the area definition. Handles both
 * 'Square metres (m²)' and 'Mixes' measurement modes.
 */
export default function BlendAreaSummary({
  area,
  index,
  totalAreas,
  measurementType,
  getWastagePercentage,
  coverage,
}) {
  const surfaceQuantity = parseFloat(area.quantity) || 0;
  const baseQuantity = parseFloat(area.base_mixes) || 0;
  const headingLabel = totalAreas > 1 ? `Area ${index + 1}` : 'Blend Area';

  // Staff-editable coverage rates come from the pricing feed. Fall back to
  // sensible defaults if the calculator was rendered before pricing loaded
  // so we never divide by undefined.
  const cov = {
    vehicle_surface_m2_per_mix: 3.5,
    vehicle_base_m2_per_mix:    3.2,
    foot_surface_m2_per_mix:    4.0,
    foot_base_m2_per_mix:       6.4,
    ...(coverage || {}),
  };

  if (measurementType === 'Square metres (m²)') {
    const hasBase = area.specification?.includes('surface and base');
    const areaM2 = surfaceQuantity;
    const wastagePercentage = getWastagePercentage();
    const areaWithWastage = areaM2 * (1 + wastagePercentage);

    let surfaceMixes;
    let baseMixes;
    if (area.specification === 'Vehicle traffic - surface only') {
      surfaceMixes = Math.ceil(areaWithWastage / cov.vehicle_surface_m2_per_mix);
    } else if (area.specification === 'Vehicle traffic - surface and base') {
      surfaceMixes = Math.ceil(areaWithWastage / cov.vehicle_surface_m2_per_mix);
      baseMixes = Math.ceil(areaWithWastage / cov.vehicle_base_m2_per_mix);
    } else if (area.specification === 'Foot traffic - surface only') {
      surfaceMixes = Math.ceil(areaWithWastage / cov.foot_surface_m2_per_mix);
    } else if (area.specification === 'Foot traffic - surface and base') {
      surfaceMixes = Math.ceil(areaWithWastage / cov.foot_surface_m2_per_mix);
      baseMixes = Math.ceil(areaWithWastage / cov.foot_base_m2_per_mix);
    }

    const isVehicle = area.specification?.includes('Vehicle');
    const surfaceCoverage = (surfaceMixes || 0) * (isVehicle ? cov.vehicle_surface_m2_per_mix : cov.foot_surface_m2_per_mix);
    const baseCoverage = baseMixes
      ? (isVehicle ? baseMixes * cov.vehicle_base_m2_per_mix : baseMixes * cov.foot_base_m2_per_mix)
      : null;

    return (
      <div className="border-2 border-[#2F5E96]/20 rounded-xl p-5 bg-[#2F5E96]/5">
        <h3 className="text-lg font-bold text-[#27292E] mb-4">{headingLabel}</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Surface Blend:</span>
            <span className="font-medium text-[#27292E]">{area.surface_blend}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Area (before wastage):</span>
            <span className="font-medium text-[#27292E]">{areaM2.toFixed(2)} m²</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Area (with wastage):</span>
            <span className="font-medium text-[#27292E]">{areaWithWastage.toFixed(2)} m²</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Specification:</span>
            <span className="font-medium text-[#27292E] text-right max-w-[60%]">{area.specification}</span>
          </div>

          <div className="pt-2 border-t-2 border-gray-300">
            <div className="flex justify-between mb-1">
              <span className="text-gray-700 font-bold">Surface Mixes:</span>
              <span className="font-bold text-[#2F5E96] text-lg">{surfaceMixes} mixes</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Coverage:</span>
              <span>{surfaceCoverage.toFixed(2)} m²</span>
            </div>
          </div>

          {hasBase && baseMixes && (
            <div className="pt-2 border-t-2 border-gray-300">
              <div className="flex justify-between mb-1">
                <span className="text-gray-700 font-bold">Base Mixes:</span>
                <span className="font-bold text-[#2F5E96] text-lg">{baseMixes} mixes</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Coverage:</span>
                <span>{baseCoverage.toFixed(2)} m²</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Mixes measurement type — show what the user entered, plus wastage.
  const wastagePercentage = getWastagePercentage();
  const surfaceMixesWithWastage = Math.ceil(surfaceQuantity * (1 + wastagePercentage));
  const baseMixesWithWastage = baseQuantity > 0
    ? Math.ceil(baseQuantity * (1 + wastagePercentage))
    : 0;

  return (
    <div className="border-2 border-[#2F5E96]/20 rounded-xl p-5 bg-[#2F5E96]/5">
      <h3 className="text-lg font-bold text-[#27292E] mb-4">{headingLabel}</h3>
      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Surface Blend:</span>
          <span className="font-medium text-[#27292E]">{area.surface_blend}</span>
        </div>

        <div className="pt-2 border-t-2 border-gray-300">
          <div className="flex justify-between mb-1">
            <span className="text-gray-700 font-bold">Surface Mixes (with wastage):</span>
            <span className="font-bold text-[#2F5E96] text-lg">{surfaceMixesWithWastage} mixes</span>
          </div>
        </div>

        {baseMixesWithWastage > 0 && (
          <div className="pt-2 border-t-2 border-gray-300">
            <div className="flex justify-between mb-1">
              <span className="text-gray-700 font-bold">Base Mixes (with wastage):</span>
              <span className="font-bold text-[#2F5E96] text-lg">{baseMixesWithWastage} mixes</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
